import { useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { calculateAnnualBreakdown } from '@amigodoclt/core';
import { useGSAP } from '@gsap/react';
import { Icon } from '../components/Icon.js';
import { SelectField, Stepper, ToggleRow } from '../components/Field.js';
import { compact, money } from '../lib/format.js';
import { salaryInputOf, useSalaryForm } from '../lib/salary-form.js';
import { gsap, useReveal } from '../lib/motion.js';

const short = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const monthOptions = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
  .map((label, index) => ({ value: String(index + 1), label }));

const colorFor = (label: string) => {
  if (label === 'INSS') return 'var(--chart-inss)';
  if (label === 'IRRF') return 'var(--chart-irrf)';
  return 'var(--accent-text)';
};

export function Charts() {
  const root = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const [form] = useSalaryForm();
  const [vacationOn, setVacationOn] = useState(true);
  const [vacationMonth, setVacationMonth] = useState(7);
  const [soldDays, setSoldDays] = useState(0);
  const [thirteenthOn, setThirteenthOn] = useState(true);
  const [thirteenthMonth, setThirteenthMonth] = useState(12);
  const [split, setSplit] = useState(true);
  useReveal(root);

  const year = useMemo(() => calculateAnnualBreakdown({
    ...salaryInputOf(form),
    ...(vacationOn ? { vacation: { month: vacationMonth, soldDays } } : {}),
    ...(thirteenthOn ? { thirteenth: { month: thirteenthMonth, split } } : {}),
  }), [form, soldDays, split, thirteenthMonth, thirteenthOn, vacationMonth, vacationOn]);

  const { salary, months, totals } = year;
  const monthlyDeductions = salary.gross - salary.net;

  const bars = [
    { label: 'Salário bruto', value: salary.gross, color: 'var(--fg)' },
    ...salary.deductions.map((line) => ({ label: line.label, value: line.amount, color: colorFor(line.label) })),
    { label: 'Salário líquido', value: salary.net, color: 'var(--green)' },
  ];
  const tallest = Math.max(...months.map((month) => month.net), 1);

  useGSAP(() => {
    if (reduce) return;
    gsap.from('.bar-fill', { scaleX: 0, duration: .9, ease: 'power3.out', stagger: .05, scrollTrigger: { trigger: '.chart-composition', start: 'top 90%' } });
    gsap.from('.annual-col i', { scaleY: 0, duration: .9, ease: 'power3.out', stagger: .04, scrollTrigger: { trigger: '.chart-annual', start: 'top 90%' } });
  }, { scope: root, dependencies: [reduce] });

  return (
    <main className="page" ref={root}>
      <div className="charts-head" data-reveal>
        <div className="page-head-copy">
          <p className="eyebrow">Projeção {year.competence}</p>
          <h1 className="page-title">O ano inteiro</h1>
          <p className="lead">
            Salário, férias e 13º somados mês a mês, sobre as mesmas premissas da calculadora de salário líquido.
          </p>
          <div className="page-actions">
            <button type="button" className="btn-secondary" onClick={() => window.print()}>
              <Icon name="print" size={18} />Imprimir projeção
            </button>
          </div>
        </div>
      </div>

      <div className="year-summary" data-reveal>
        <div className="year-cell">
          <span><Icon name="payments" size={14} />Bruto no ano</span>
          <strong>{compact.format(totals.gross)}</strong>
          <small>{money(salary.gross)} por mês</small>
        </div>
        <div className="year-cell deductions">
          <span><Icon name="do_not_disturb_on" size={14} />Descontos no ano</span>
          <strong>−{compact.format(totals.deductions)}</strong>
          <small>{money(monthlyDeductions)} por mês · {totals.gross > 0 ? ((totals.deductions / totals.gross) * 100).toFixed(1) : '0,0'}% do bruto</small>
        </div>
        <div className="year-cell net">
          <span><Icon name="savings" size={14} />Líquido no ano</span>
          <strong>{compact.format(totals.net)}</strong>
          <small>{money(totals.net / 12)} por mês na média</small>
        </div>
        <div className="year-cell">
          <span><Icon name="account_balance" size={14} />FGTS dos 12 salários</span>
          <strong>{compact.format(totals.fgts)}</strong>
          <small>Depósito do empregador, não sai do líquido</small>
        </div>
      </div>

      <section className="glass projection-controls" data-reveal>
        <h2 className="section-title"><span className="section-icon"><Icon name="tune" size={20} /></span>O que entra na projeção</h2>
        <div className="field-grid">
          <div className="control-group">
            <ToggleRow label="Incluir férias" icon="beach_access" value={vacationOn} onChange={setVacationOn} />
            {vacationOn ? (
              <div className="field-grid">
                <SelectField label="Mês das férias" value={String(vacationMonth)} onChange={(value) => setVacationMonth(Number(value))} options={monthOptions} />
                <Stepper label="Dias vendidos (abono)" value={soldDays} onChange={setSoldDays} min={0} max={10} step={1} />
              </div>
            ) : null}
          </div>
          <div className="control-group">
            <ToggleRow label="Incluir 13º" icon="redeem" value={thirteenthOn} onChange={setThirteenthOn} />
            {thirteenthOn ? (
              <>
                <div className="field-grid">
                  <SelectField label="Mês da parcela final" value={String(thirteenthMonth)} onChange={(value) => setThirteenthMonth(Number(value))} options={monthOptions} />
                </div>
                <ToggleRow label="Adiantar 1ª parcela no mês anterior" icon="schedule" value={split} onChange={setSplit} />
              </>
            ) : null}
          </div>
        </div>
        <p className="helper">
          <Icon name="info" size={16} />
          No mês de férias entra apenas o que excede o salário, porque quem tira 30 dias não recebe salário e férias cheios.
        </p>
      </section>

      <div className="charts-grid">
        <section className="chart-card glass chart-composition" data-reveal>
          <h2><span className="section-icon"><Icon name="stacked_bar_chart" size={20} /></span>Do bruto ao líquido no mês</h2>
          {bars.map((bar) => (
            <div className="bar-row" key={bar.label}>
              <div className="bar-head">
                <span><i className="bar-swatch" style={{ background: bar.color }} />{bar.label}</span>
                <strong>{money(bar.value)}</strong>
              </div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${Math.min(100, (bar.value / Math.max(salary.gross, 1)) * 100)}%`, background: bar.color }} />
              </div>
            </div>
          ))}
        </section>

        <section className="chart-card glass chart-annual" data-reveal>
          <h2><span className="section-icon"><Icon name="calendar_view_month" size={20} /></span>Líquido mês a mês</h2>
          <div className="annual">
            {months.map((month, index) => (
              <div className={`annual-col${month.items.length > 0 ? ' peak' : ''}`} key={month.month}>
                <i style={{ height: `${Math.max(8, (month.net / tallest) * 100)}%` }} title={`${short[index]}: ${money(month.net)}`} />
                <span>{short[index]}</span>
              </div>
            ))}
          </div>
          <p className="annual-total">Líquido total no ano<strong>{money(totals.net)}</strong></p>
          <span className="helper"><Icon name="info" size={16} />As colunas em vermelho são os meses com férias ou 13º.</span>
        </section>
      </div>

      <section className="glass chart-card" data-reveal>
        <h2><span className="section-icon"><Icon name="table_rows" size={20} /></span>Mês a mês</h2>
        <div className="year-table-scroll">
          <div className="year-table">
            <div className="year-row head">
              <span>Mês</span><span>Bruto</span><span>Descontos</span><span>Líquido</span><span>Entra a mais</span>
            </div>
            {months.map((month, index) => (
              <div className={`year-row${month.items.length > 0 ? ' marked' : ''}`} key={month.month}>
                <span>{short[index]}</span>
                <span>{money(month.gross)}</span>
                <span className="negative">−{money(month.deductions)}</span>
                <span className="net">{money(month.net)}</span>
                <span className="extras">{month.items.map((item) => item.label).join(' · ') || '—'}</span>
              </div>
            ))}
            <div className="year-row total">
              <span>Ano</span>
              <span>{money(totals.gross)}</span>
              <span className="negative">−{money(totals.deductions)}</span>
              <span className="net">{money(totals.net)}</span>
              <span className="extras">{year.extras.length > 0 ? `${year.extras.length} entrada${year.extras.length === 1 ? '' : 's'} extra` : '—'}</span>
            </div>
          </div>
        </div>
        <details className="result-details">
          <summary><Icon name="history_edu" size={18} />Memória e premissas<Icon name="expand_more" size={18} className="caret" /></summary>
          <div className="body">
            <ul>
              {year.memory.map((item) => <li key={item}>{item}</li>)}
              {year.warnings.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </details>
      </section>
    </main>
  );
}
